import { FC, useEffect, useState } from "react"
import { toast } from "react-toastify"

import { useTranslation } from "react-i18next"

import { Modal } from "components/modals/Modal"

import { cmsSeoService } from "services/cms-seo/cms-seo.service"
import { CmsRedirect, CmsRedirectCode, CmsRedirectInput } from "services/cms-seo/type"

import { normalisePath } from "./helpers/format"
import { Field, FieldRow, ghostButtonClass, inputClass, modalBoxClass, primaryButtonClass} from "./CmsUi"

export type RedirectFormState = { kind: "none" } | { kind: "create" } | { kind: "edit"; record: CmsRedirect }

const EMPTY: CmsRedirectInput = { source: "", target: "", code: 301 }

interface RedirectModalProps {
  state: RedirectFormState
  onClose: () => void
  onSaved: () => void
}

export const RedirectModal: FC<RedirectModalProps> = ({ state, onClose, onSaved }) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const [form, setForm] = useState<CmsRedirectInput>(EMPTY)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (state.kind === "edit") setForm({ source: state.record.source, target: state.record.target, code: state.record.code })
    if (state.kind === "create") setForm(EMPTY)
  }, [state])

  const submit = async () => {
    // A wildcard source keeps its trailing "/*", so it is normalised without it.
    const wildcard = form.source.trim().endsWith("/*")
    const source = wildcard ? `${normalisePath(form.source.trim().slice(0, -2))}/*` : normalisePath(form.source)
    const target = normalisePath(form.target)

    if (source === target) {
      toast.error(t("cmsSeo.redirectSelfLoop", { defaultValue: "A redirect cannot point at itself" }) as string)
      return
    }

    setSaving(true)
    const payload: CmsRedirectInput = { source, target, code: form.code }
    const saved =
      state.kind === "edit" ? await cmsSeoService.updateRedirect(state.record.id, payload) : await cmsSeoService.createRedirect(payload)
    setSaving(false)
    if (saved) {
      toast.success(t("cmsSeo.redirectSaved", { defaultValue: "Redirect saved" }) as string)
      onSaved()
    }
  }

  return (
    <Modal open={state.kind !== "none"} handler={onClose}>
      <div className={`${modalBoxClass} max-w-xl`}>
        <h3 className="mb-4 text-lg font-semibold">
          {state.kind === "edit"
            ? t("cmsSeo.editRedirect", { defaultValue: "Edit redirect" })
            : t("cmsSeo.addRedirect", { defaultValue: "Add redirect" })}
        </h3>

        <FieldRow>
          <Field
            label={t("cmsSeo.redirect.source", { defaultValue: "Source path" })}
            hint={t("cmsSeo.redirect.sourceHint", { defaultValue: "End with /* to match everything under a prefix" })}
          >
            <input
              className={`${inputClass} font-mono`}
              value={form.source}
              onChange={(event) => setForm((prev) => ({ ...prev, source: event.target.value }))}
              placeholder="/old-page"
            />
          </Field>
        </FieldRow>

        <FieldRow>
          <Field label={t("cmsSeo.redirect.target", { defaultValue: "Target path" })}>
            <input
              className={`${inputClass} font-mono`}
              value={form.target}
              onChange={(event) => setForm((prev) => ({ ...prev, target: event.target.value }))}
              placeholder="/new-page"
            />
          </Field>
        </FieldRow>

        <FieldRow>
          <Field
            label={t("cmsSeo.redirect.code", { defaultValue: "Status code" })}
            hint={t("cmsSeo.redirect.codeHint", {
              defaultValue: "301 passes ranking to the target and is cached by browsers. Use 302 only while a move is temporary."
            })}
          >
            <select
              className={inputClass}
              value={form.code}
              onChange={(event) => setForm((prev) => ({ ...prev, code: Number(event.target.value) as CmsRedirectCode }))}
            >
              <option value={301}>301 — permanent</option>
              <option value={302}>302 — temporary</option>
            </select>
          </Field>
        </FieldRow>

        <div className="modal-action flex-wrap">
          <button type="button" className={ghostButtonClass} onClick={onClose}>
            {t("common.cancel", { defaultValue: "Cancel" })}
          </button>
          <button type="button" className={primaryButtonClass} disabled={saving} onClick={submit}>
            {t("common.save", { defaultValue: "Save" })}
          </button>
        </div>
      </div>
    </Modal>
  )
}
